import React, { useState } from "react";
import { FacebookPostProps } from "../types";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { Loader, MessageSquare, Share2, ThumbsUp } from "lucide-react";
import { Button } from "../../misc/Button";
import FacebookMediaGalleryOverlay from "./FacebookMediaGalleryOverlay";
import { MediaType } from "../../../types/media";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { GiMicrophone } from "react-icons/gi";
import { calculateFittedFontSize } from "../../../utils/autoFitFont";
import PostVideoView from "../PostVideoView";
import { isLeadGeneration, withoutLeadGenerationBody } from "../util/leadGenerationPost";

const FacebookPost: React.FC<FacebookPostProps> = ({ post: sourcePost, setLoading, hideDatetime = false, contentCharLimit }) => {
    const post = withoutLeadGenerationBody(sourcePost);
    const user = useSelector((state: RootState) => state.auth.user);
    const mediaCount = post.media?.length || 0;

    const [showGallery, setShowGallery] = useState(false);
    const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
    const [isExpanded, setIsExpanded] = useState(false);

    const renderMediaItem = (index: number, className: string, inGrid = false) => {
        const media = post.media![index];
        if (!media) return null;

        return (
            <div
                className={`cursor-pointer ${className} bg-black overflow-hidden relative ${inGrid ? "aspect-[4/3]" : "flex items-center justify-center max-h-[500px]"}`}
                onClick={() => handleMediaClick(index)}
            >
                {media.type === MediaType.AssetVideo ||
                media.type === MediaType.ListingVideo ? (
                    !inGrid ? (
                        <PostVideoView
                            src={media.signedUrl || media.url || ""}
                            metadata={media.videoMetadata}
                            thumbnail={media?.thumbnail}
                            isProcessing={media?.isProcessing}
                            autoPlay={isMobile}
                        />
                    ) : (
                        <div className="w-full aspect-[16/9] bg-black overflow-hidden relative">
                            {media?.isProcessing ? (
                                <>
                                    {media?.thumbnail ? (
                                        <img
                                            src={media?.thumbnail}
                                            alt="Media"
                                            className="w-full h-full object-contain cursor-pointer"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-black" />
                                    )}
                                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded">
                                        <span className="text-white text-sm font-medium">
                                            Video Processing
                                        </span>
                                        <Loader className="ml-2 animate-spin text-secondary" />
                                    </div>
                                </>
                            ) : (
                                <video
                                    muted
                                    src={media.signedUrl || media.url || ""}
                                    className="w-full h-full object-contain"
                                    controls
                                    autoPlay={isMobile}
                                />
                            )}
                        </div>
                    )
                ) : (
                    <img
                        src={media.signedUrl}
                        alt={`Media ${index + 1}`}
                        className={inGrid ? "absolute inset-0 w-full h-full object-cover" : "w-full h-auto max-h-[500px] object-contain"}
                    />
                )}
            </div>
        );
    };

    const handleMediaClick = (index: number) => {
        const media = post.media[index];

        if (
            media.type === MediaType.AssetVideo ||
            media.type === MediaType.ListingVideo
        ) {
            return;
        }

        if (
            (isMobile &&
                post?.media[index].type !== MediaType.AssetVideo &&
                post?.media[index].type !== MediaType.ListingVideo) ||
            !isMobile
        ) {
            setGalleryInitialIndex(index);
            setShowGallery(true);
        }
    };

    const renderMedia = () => {
        // Check if this is a Lead Generation post (either type "Lead Generation" or preview with styling)
        const isLeadGenerationPost = post.type === 'Lead Generation' || 
                                    (post.type === 'preview' && post.background && post.textColor);

        // For Lead Generation posts: if image is generated, display it instead of re-rendering styled block
        if (isLeadGenerationPost && post.media && post.media.length > 0 && post.media[0].signedUrl) {
            return <div className="w-full">{renderMediaItem(0, "w-full")}</div>;
        }

        // If backgroundColor and textColor are present, render styled content block instead of media
        if (post.background && post.textColor) {
            // Use imageText for the graphic if available, otherwise use content
            const graphicText = (
                typeof ((post as any).imageText || post.content) === "string"
                    ? ((post as any).imageText || post.content)
                    : Array.isArray(((post as any).imageText || post.content))
                    ? ((post as any).imageText || post.content).join("\n\n")
                    : ""
            ).trim();
            const content = graphicText;

            // Helper function to get background style
            const getBackgroundStyle = (bg: any) => {
                if (!bg) return undefined;
                if (bg.type === 'gradient') {
                    return `linear-gradient(${bg.direction || 'to right'}, ${bg.from || '#000000'}, ${bg.to || '#FFFFFF'})`;
                }
                return bg.color || '#000000';
            };

            // Platform-specific dimensions (matching backend)
            const platformDimensions = { width: 1200, height: 630 }; // Facebook dimensions
            const maxWidth = isMobile ? 400 : 600;
            const scale = maxWidth / platformDimensions.width;
            const displayWidth = platformDimensions.width * scale;
            const displayHeight = platformDimensions.height * scale;

            if (isMobile) {
                // Mobile: fluid layout — fills container width, locked to Facebook 1200:630 aspect ratio
                // Use conservative mobile estimate (280x147) accounting for ~8% padding on each side
                const mobileFontSize = calculateFittedFontSize(content, post.fontSize || 18, post.fontFamily, 280, 147);
                return (
                    <div
                        className="w-full flex items-center
                         justify-center rounded-lg"
                        style={{
                            aspectRatio: "1200/630",
                            background: getBackgroundStyle(post.background),
                            color: post.textColor,
                            fontFamily: post.fontFamily ? `"${post.fontFamily}", sans-serif` : undefined,
                            padding: "8%",
                        }}
                    >
                        <p
                            className="font-bold text-center break-words"
                            style={{
                                fontSize: `${mobileFontSize}px`,
                                lineHeight: 1.5,
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                                width: "100%",
                            }}
                        >
                            {content}
                        </p>
                    </div>
                );
            }

            // Desktop: original pixel-based rendering (unchanged)
            const baseFontSize = post.fontSize || 18;
            const fittedFontSize = calculateFittedFontSize(content, baseFontSize, post.fontFamily, displayWidth, displayHeight);
            const horizontalPadding = Math.round(displayWidth * 0.08);
            const verticalPadding = Math.round(displayHeight * 0.08);

            return (
                <div className="w-full flex justify-center"
                     style={{ 
                         maxWidth: `${maxWidth}px`,
                         margin: '0 auto'
                     }}>
                    <div className="flex items-center justify-center rounded-lg overflow-hidden"
                         style={{
                             background: getBackgroundStyle(post.background),
                             color: post.textColor,
                             fontFamily: post.fontFamily ? `"${post.fontFamily}", sans-serif` : undefined,
                             width: `${displayWidth}px`,
                             height: `${displayHeight}px`,
                             paddingLeft: `${horizontalPadding}px`,
                             paddingRight: `${horizontalPadding}px`,
                             paddingTop: `${verticalPadding}px`,
                             paddingBottom: `${verticalPadding}px`,
                         }}>
                        <p className="font-bold text-center"
                           style={{
                               fontSize: `${fittedFontSize}px`,
                               lineHeight: 1.5,
                           }}>
                            {content}
                        </p>
                    </div>
                </div>
            );
        }

        if (!post.media || mediaCount === 0) return null;

        // For a single item, just full-width
        if (mediaCount === 1) {
            return <div className="w-full">{renderMediaItem(0, "w-full")}</div>;
        }

        // Two items side by side
        if (mediaCount === 2) {
            return (
                <div className="grid grid-cols-2 gap-1">
                    {post.media.map((_, idx) => (
                        <React.Fragment key={idx}>
                            {renderMediaItem(idx, "w-full", true)}
                        </React.Fragment>
                    ))}
                </div>
            );
        }

        // Three items: first full width, bottom two side by side
        if (mediaCount === 3) {
            return (
                <div className="grid grid-cols-2 gap-1">
                    {renderMediaItem(0, "col-span-2 w-full", true)}
                    {renderMediaItem(1, "w-full", true)}
                    {renderMediaItem(2, "w-full", true)}
                </div>
            );
        }

        // Four or more: show four with extra count
        const displayCount = Math.min(mediaCount, 4);
        const extraCount = mediaCount - 4;
        return (
            <div className="grid grid-cols-2 gap-1">
                {Array.from({ length: displayCount }).map((_, idx) => {
                    const showOverlay = idx === 3 && extraCount > 0;

                    return (
                        <div key={idx} className="relative">
                            {renderMediaItem(idx, "w-full", true)}

                            {showOverlay && (
                                <button
                                    type="button"
                                    onClick={() => handleMediaClick(idx)}
                                    className="absolute inset-0 bg-black/50 flex items-center justify-center"
                                >
                                    <span className="text-white text-xl font-semibold">
                                        +{extraCount}
                                    </span>
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const isMobile = useIsMobile(900);
    const CHARACTER_LIMIT = isMobile ? 150 : 300;

    // Function to toggle the expanded state for content (and footer if in-post)
    const toggleExpand = () => {
        setIsExpanded((prev) => !prev);
    };

    // Separating the footer-rendering logic into its own function
    const renderFooter = () => {
        const paragraphs = post?.footer?.split(/\n\n/) || [];
        return (
            <>
                {paragraphs.map((para, index) => (
                    <p key={index} className="mt-2 break-words overflow-hidden" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {para.split("\n").map((line, i, arr) => (
                            <React.Fragment key={i}>
                                {line}
                                {i < arr.length - 1 && <br />}
                            </React.Fragment>
                        ))}
                    </p>
                ))}
            </>
        );
    };

    // Render the post content. For footerType "in-post", include the footer within the post content.
    // For footerType "comment", do not include the footer here.
    const renderContent = () => {
        // Normalize content to a string and trim whitespace
        const rawContent = (
            typeof post.content === "string"
                ? post.content
                : Array.isArray(post.content)
                ? post.content.join("\n\n")
                : ""
        ).trim();
        // Split at platform character limit if provided
        const withinContent = contentCharLimit ? rawContent.substring(0, contentCharLimit) : rawContent;
        const overflowContent = contentCharLimit && rawContent.length > contentCharLimit ? rawContent.substring(contentCharLimit) : "";
        const content = withinContent;
        if (!content && isLeadGeneration(post)) {
            return post.footerType === "in-post" && post.includeFooter && post.footer ? renderFooter() : null;
        }
        const paragraphs = content.split(/\n\n+/);
        const isContentTruncated = content.length > CHARACTER_LIMIT;

        const overflowBlock = overflowContent ? (
            <span className="bg-red-100 line-through text-red-600">{overflowContent}</span>
        ) : null;

        if (!isContentTruncated) {
            return (
                <>
                    {paragraphs.map((para, index) => (
                        <p key={index} className="mb-2 min-h-[24px]">
                            {para}
                        </p>
                    ))}
                    {overflowBlock}
                    {/* Only include footer in post content if type is "in-post" */}
                    {post.footerType === "in-post" &&
                        post.includeFooter &&
                        !isExpanded && (
                            <button
                                onClick={toggleExpand}
                                className="text-blue-500 hover:underline ml-1"
                                aria-label="Show more"
                            >
                                Show More
                            </button>
                        )}
                    {post.footerType === "in-post" &&
                        post.includeFooter &&
                        isExpanded &&
                        post.footer && (
                            <>
                                <div className="mt-2">{renderFooter()}</div>
                                <button
                                    onClick={toggleExpand}
                                    className="text-blue-500 hover:underline ml-1"
                                    aria-label="Show less"
                                >
                                    Show Less
                                </button>
                            </>
                        )}
                </>
            );
        }

        if (isExpanded) {
            return (
                <>
                    {paragraphs.map((para, index) => (
                        <p key={index} className="mb-2 min-h-[24px]">
                            {para}
                        </p>
                    ))}
                    {overflowBlock}
                    {post.footerType === "in-post" &&
                        post.includeFooter &&
                        post.footer && (
                            <div className="mt-2">{renderFooter()}</div>
                        )}
                    <button
                        onClick={toggleExpand}
                        className="text-blue-500 hover:underline ml-1"
                        aria-label="Show less"
                    >
                        Show Less
                    </button>
                </>
            );
        } else {
            let truncatedContent = content.slice(0, CHARACTER_LIMIT);
            const lastSpace = truncatedContent.lastIndexOf(" ");
            if (lastSpace > 0) {
                truncatedContent = truncatedContent.slice(0, lastSpace);
            }
            const truncatedParagraphs = truncatedContent
                .split(/\n+/)
                .map((para, index) => (
                    <p key={index} className="mb-2 min-h-[24px]">
                        {para}
                    </p>
                ));
            return (
                <>
                    {truncatedParagraphs}
                    <button
                        onClick={toggleExpand}
                        className="text-blue-500 hover:underline ml-1"
                        aria-label="Read more"
                    >
                        Read More
                    </button>
                </>
            );
        }
    };

    return (
        <div className="max-w-[680px] mx-auto bg-white shadow-xl rounded-lg overflow-hidden">
            {/* Post Header */}
            <div className="p-4 flex items-start justify-between">
                <div className="flex gap-2">
                    {post?.userImage || user?.profileImage || user?.photoURL ? (
                        <img
                            src={
                                post?.userImage ||
                                user?.profileImage ||
                                user?.photoURL ||
                                undefined
                            }
                            width={40}
                            height={40}
                            alt="Profile"
                            className="rounded-full object-cover"
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const fallback = target.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <span 
                        className="text-sm font-semibold flex items-center justify-center w-10 h-10 min-w-[40px] min-h-[40px] bg-gray-300 rounded-full"
                        style={{ display: (post?.userImage || user?.profileImage || user?.photoURL) ? 'none' : 'flex' }}
                    >
                        {user && user.nameFirst && user.nameLast
                            ? user.nameFirst[0].toUpperCase() + user.nameLast[0].toUpperCase()
                            : post?.displayName 
                            ? post.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                            : 'U'}
                    </span>

                    <div>
                        <div className="flex items-center gap-1">
                            <span className="font-semibold hover:underline cursor-pointer">
                                {post?.pageName && post?.profileUrl ? (
                                    <a
                                        href={post.profileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-semibold hover:underline cursor-pointer"
                                    >
                                        {post.pageName}
                                    </a>
                                ) : (
                                    `${user?.nameFirst} ${user?.nameLast}`
                                )}
                            </span>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            {!hideDatetime && (
                                <span>
                                    {`${new Date(
                                        post.scheduledFor
                                    ).toLocaleDateString("en-US", {
                                        month: "long",
                                        day: "numeric",
                                    })} at ${new Date(
                                        post.scheduledFor
                                    ).toLocaleTimeString("en-US", {
                                        hour: "numeric",
                                        minute: "2-digit",
                                        hour12: true,
                                    })}`}
                                </span>
                            )}
                            {/* <Globe2 className="w-3 h-3" /> */}
                        </div>
                    </div>
                </div>
            </div>

            {/* Post Content */}
            <div className="px-4 pb-3">
                {((post as any)?.contentType === "Caption Video" || (post as any)?.type === "Caption Video") &&
                !post.content &&
                (post?.status?.toLowerCase() === "draft" || post?.status?.toLowerCase() === "videoprocessing") ? (
                    <div className="space-y-2 py-1">
                        <div className="h-3 bg-gray-200 rounded w-3/4 shimmer-bar" />
                        <div className="h-3 bg-gray-200 rounded w-1/2 shimmer-bar" />
                    </div>
                ) : (
                    <div className="whitespace-pre-line break-words overflow-hidden" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>{renderContent()}</div>
                )}
            </div>

            {/* Media Grid */}
            <div className="px-4">{renderMedia()}</div>

            {/* Engagement Stats */}
            <div className="px-4 py-2 flex items-center justify-between text-sm text-muted-foreground border-b">
                <div className="flex items-center gap-1">
                    <div className="flex -space-x-1">
                        <div className="w-4 h-4 rounded-full bg-blue-500 border-2 border-white" />
                        <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-white" />
                        <div className="w-4 h-4 rounded-full bg-yellow-500 border-2 border-white" />
                    </div>
                    <span>44</span>
                </div>
                {!isMobile && (
                    <div className="flex gap-3">
                        <span>14 comments</span>
                        <span>8 shares</span>
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            <div className="px-2 py-1 flex items-center justify-between border-b">
                <Button
                    variant="ghost"
                    className={`flex-1 flex items-center justify-center ${isMobile ? 'gap-1 px-1' : 'gap-2'}`}
                >
                    <ThumbsUp className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} />
                    {!isMobile && <span>Like</span>}
                </Button>
                <Button
                    variant="ghost"
                    className={`flex-1 flex items-center justify-center ${isMobile ? 'gap-1 px-1' : 'gap-2'}`}
                >
                    <MessageSquare className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} />
                    {!isMobile && <span>Comment</span>}
                </Button>
                <Button
                    variant="ghost"
                    className={`flex-1 flex items-center justify-center ${isMobile ? 'gap-1 px-1' : 'gap-2'}`}
                >
                    <Share2 className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} />
                    {!isMobile && <span>Share</span>}
                </Button>
            </div>

            {/* Footer rendered as a comment (for footerType "comment") */}
            {post.footerType === "comment" && post.includeFooter && post.footer && (
                <div className="px-4 py-2 flex gap-2 items-start border-t overflow-hidden">
                    <img
                        src={
                            post?.userImage ||
                            user?.profileImage ||
                            user?.photoURL ||
                            '/placeholder-avatar.svg'
                        }
                        alt="Profile"
                        className="w-8 h-8 rounded-full object-cover"
                        onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const fallback = target.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                        }}
                    />
                    <span 
                        className="text-sm font-semibold flex items-center justify-center w-10 h-10 min-w-[32px] min-h-[32px] bg-gray-300 rounded-full"
                        style={{ display: (post?.userImage || user?.profileImage || user?.photoURL) ? 'none' : 'flex' }}
                    >
                        {user && user.nameFirst && user.nameLast
                            ? user.nameFirst[0].toUpperCase() + user.nameLast[0].toUpperCase()
                            : post?.displayName 
                            ? post.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                            : 'U'}
                    </span>
                    <div className="flex flex-col items-start justify-center min-w-0 overflow-hidden">
                        <div className="bg-gray-100 rounded-lg p-2 overflow-hidden" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                            <div>
                                <GiMicrophone className="inline-block mr-1 text-neutral-500" />
                                <span className="text-sm text-neutral-500">
                                    Author
                                </span>
                            </div>
                            <span className="font-semibold">
                                {post?.pageName && post?.profileUrl ? (
                                    <a
                                        href={post.profileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-semibold hover:underline cursor-pointer"
                                    >
                                        {post.pageName}
                                    </a>
                                ) : (
                                    `${user?.nameFirst} ${user?.nameLast}`
                                )}
                            </span>
                            {renderFooter()}
                        </div>
                        <div className="flex flex-row items-center justify-start gap-4 mt-1 px-2">
                            {/* Time in hr / days  as in 1hr or 1d etc*/}
                            <span className="text-sm text-muted-foreground">
                                {(() => {
                                    const scheduledDate = new Date(
                                        post.scheduledFor
                                    );
                                    const now = new Date();
                                    const diffMs = Math.abs(
                                        now.getTime() - scheduledDate.getTime()
                                    );
                                    const diffMins = Math.floor(diffMs / 60000);

                                    if (diffMins < 60) {
                                        return `${diffMins}m`;
                                    }

                                    const diffHours = Math.floor(diffMins / 60);
                                    if (diffHours < 24) {
                                        return `${diffHours}hr`;
                                    }

                                    const diffDays = Math.floor(diffHours / 24);
                                    return `${diffDays}d`;
                                })()}
                            </span>
                            <span className="text-sm text-muted-foreground">
                                Like
                            </span>
                            <span className="text-sm text-muted-foreground">
                                Reply
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {showGallery && (
                <FacebookMediaGalleryOverlay
                    media={post.media}
                    initialIndex={galleryInitialIndex}
                    onClose={() => setShowGallery(false)}
                />
            )}
        </div>
    );
};

export default FacebookPost;
